/**
 * 子门户白标 API — R3-Sprint5(对接 backend Sprint 3 β + Sprint 4 / 5)。
 *
 * Sprint 5 接管:
 * - GET /public/branding/{slug}   无 JWT 公开端点 — 登录页 + 顶 logo 用
 * - GET /tenant-branding          需 JWT branding:read — 本租户已登录视图
 *
 * 注意:public 端点必须用裸 fetch / 独立 axios 实例 — apiClient 拦截器会:
 *  - 401 → 跳 /login(我们不希望未登录访问 /public/branding 触发登录跳转)
 *  - 注入 Authorization header(未登录时无 token 也无副作用,但语义更干净)
 */

import axios from 'axios';
import { apiGet } from './client';

/** 公开品牌信息(/public/branding/{slug} 返回的字段子集 — TenantBrandingPublicOut)。 */
export interface PublicBranding {
  tenant_slug: string;
  company_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  login_bg_url: string | null;
  font_family: string | null;
  tagline: string | null;
  icp_filing: string | null;
}

/** 本租户完整品牌信息(GET /tenant-branding,含 updated_by/at 等内部字段)。 */
export interface TenantBranding extends PublicBranding {
  tenant_id: string;
  updated_at: string;
  updated_by: string | null;
}

const _publicClient = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
  // 不携 token,不走 apiClient 拦截器(避免 401 跳登录)
});

_publicClient.interceptors.response.use((res) => {
  const body = res.data as { code?: number; message?: string; data?: unknown };
  if (body && body.code !== undefined) {
    if (body.code !== 0) {
      return Promise.reject(new Error(body.message || `code=${body.code}`));
    }
    res.data = body.data;
  }
  return res;
});

/** 拉公开品牌信息(无 JWT)。slug 不存在 → reject。 */
export async function getPublicBranding(slug: string): Promise<PublicBranding> {
  const res = await _publicClient.get<PublicBranding>(`/public/branding/${encodeURIComponent(slug)}`);
  return res.data;
}

/** 拉本租户品牌(需登录,branding:read 权限)。 */
export async function getTenantBranding(): Promise<TenantBranding> {
  return apiGet<TenantBranding>('/tenant-branding');
}

/**
 * R3-Sprint6 Sec I1 follow-up — 后端 RESERVED 子域同源响应。
 *
 * 后端 `GET /api/v1/public/branding/_reserved`(无 JWT)返回:
 *   { reserved: ['admin','www','mail',...], slug_pattern: '[a-z0-9][a-z0-9-]{2,31}' }
 *
 * 设计目标:消除 FE 本地 RESERVED 列表与 backend `app.core.tenant._RESERVED_SUBDOMAINS`
 * 的双维护(任一加新词都要改两边,极易漂移)。
 */
export interface ReservedSubdomainsResponse {
  reserved: string[];
  /** 与 backend `_SLUG_RE.pattern` 一致 — 形如 `[a-z0-9][a-z0-9-]{2,31}`(不含 `^$` 锚)。 */
  slug_pattern: string;
}

/**
 * 拉 RESERVED 子域 + slug regex(无 JWT)。
 * 调用方:App 启动时 fire-and-forget,失败不阻塞主 branding flow。
 * 走与 `getPublicBranding` 同一个 `_publicClient`(不带 token,不触发 401 跳转)。
 */
export async function getReservedSubdomains(): Promise<ReservedSubdomainsResponse> {
  const res = await _publicClient.get<ReservedSubdomainsResponse>('/public/branding/_reserved');
  return res.data;
}

// ------ in-memory cache(模块级单例,TTL 1h)------
let _cachedReserved: ReservedSubdomainsResponse | null = null;
let _cachedReservedTs = 0;
const _RESERVED_CACHE_TTL_MS = 60 * 60 * 1000; // 1 小时

/**
 * 带 1h cache 的 RESERVED 拉取 — 多组件并发调用只产生一次网络请求。
 * 失败不缓存(下次调用会重试)。
 */
export async function getReservedSubdomainsCached(): Promise<ReservedSubdomainsResponse> {
  const now = Date.now();
  if (_cachedReserved && now - _cachedReservedTs < _RESERVED_CACHE_TTL_MS) {
    return _cachedReserved;
  }
  const fresh = await getReservedSubdomains();
  _cachedReserved = fresh;
  _cachedReservedTs = now;
  return fresh;
}

/** 测试 / 故障演练用 — 清空 cache 强制下次重新拉。 */
export function _resetReservedSubdomainsCache(): void {
  _cachedReserved = null;
  _cachedReservedTs = 0;
}

/**
 * 本地硬编码 RESERVED fallback — 网络拉取失败时用。
 *
 * R3-Sprint6 Sec I1:首选 `getReservedSubdomainsCached()` 拉后端权威列表
 * (`reservedOverride` 参数注入),本地副本仅作降级兜底。**禁止删除** — 网络
 * 失败 / 启动期未 ready 时仍要能安全解析子域。
 */
const _RESERVED_FALLBACK: ReadonlySet<string> = new Set([
  'www', 'api', 'admin', 'platform', 'static', 'cdn', 'metrics',
  'mail', 'smtp', 'imap', 'pop', 'pop3', 'webmail', 'vpn', 'ftp', 'sftp',
  'dev', 'test', 'staging', 'stage', 'uat', 'docs', 'doc', 'blog',
  'assets', 'files', 'media', 'img', 'image', 'video',
]);

/**
 * 从 window.location.host 解析子域 tenant_slug。
 * - `demo.incubator.iaiot.cloud` → `demo`
 * - `incubator.iaiot.cloud` → null(主域)
 * - `localhost:5173` → null(dev 主机)
 * - 保留子域(www/api/admin/...) → null(与 backend _RESERVED_SUBDOMAINS 对齐)
 *
 * @param reservedOverride  R3-Sprint6 Sec I1 — 后端权威 RESERVED Set。
 *   传入则替代本地 `_RESERVED_FALLBACK`,消除双维护。未传(或网络失败前)走 fallback。
 */
export function parseSubdomainSlug(reservedOverride?: ReadonlySet<string>): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  const baseDomain = (
    import.meta.env.VITE_APP_BASE_DOMAIN ?? 'incubator.iaiot.cloud'
  ).toLowerCase();
  if (host === baseDomain) return null;
  const suffix = '.' + baseDomain;
  if (!host.endsWith(suffix)) return null;
  const sub = host.slice(0, -suffix.length);
  const reserved = reservedOverride ?? _RESERVED_FALLBACK;
  if (reserved.has(sub)) return null;
  // 与 backend _SLUG_RE 一致:[a-z0-9][a-z0-9-]{2,31}
  // 注:此处仍硬编码 — backend `slug_pattern` 字段供 Implementer 在表单校验场景动态消费。
  if (!/^[a-z0-9][a-z0-9-]{2,31}$/.test(sub)) return null;
  return sub;
}
